/**
 * Multi-camera angle helpers.
 *
 * Two camera angles on one analysis are two capture SESSIONS sharing a
 * `session_group_id` — deliberately not two clips on one session, because the
 * clip route holds exactly one file per session and a later upload replaces the
 * earlier one. Everything that re-groups those sessions into "one analysis"
 * lives here, so the coach page, the student page and the public share page
 * cannot drift apart.
 *
 * Pure functions, no React — testable without a renderer.
 */

/** Display name for a view: the coach's label, else a 1-based fallback. */
export function viewName(v, idx = 0) {
  const raw = (v?.view_label || "").trim();
  if (raw) return raw;
  return `View ${((v?.view_index ?? idx) || 0) + 1}`;
}

/** The group key for a session, tolerating pre-multi-view rows. */
export function groupKey(s) {
  return s?.session_group_id || s?.id;
}

/**
 * Collapse a session list to ONE entry per analysis.
 *
 * Keeps the PRIMARY view (lowest view_index), not the newest: a second angle is
 * recorded after the first, so "newest wins" would relabel every two-angle
 * analysis with its second camera and show the wrong clip on the card.
 *
 * Insertion order of `rows` is preserved (callers pass newest-first), so the
 * list stays sorted by the primary view's date.
 */
export function groupAnalyses(rows = []) {
  const byGroup = new Map();
  for (const s of rows) {
    const gid = groupKey(s);
    const cur = byGroup.get(gid);
    if (!cur || (s.view_index ?? 0) < (cur.view_index ?? 0)) byGroup.set(gid, s);
  }
  return Array.from(byGroup.values());
}

const labelHas = (views, ...needles) =>
  views.some((v) => {
    const l = (v?.view_label || "").toLowerCase();
    return needles.some((n) => l.includes(n));
  });

/**
 * Which angle to trust for which metric.
 *
 * These aren't preferences — they follow from how each measurement is computed.
 * A forward swing only produces useful image-horizontal motion at an angle off
 * the shot line, and torso rotation is unresolvable from a dead-on view. So the
 * two views are attributed, never averaged: a mean across two view geometries
 * describes neither camera.
 */
export function viewGuidance(views = []) {
  if (!Array.isArray(views) || views.length < 2) return { pairs: [], notes: [] };
  const net = labelHas(views, "net");
  const behind = labelHas(views, "base", "behind");
  const side = labelHas(views, "side", "on");

  const pairs = [];
  if (net) {
    pairs.push({
      metrics: "Paddle up · ready position · stroke path",
      from: "the net-side view",
      why: "the paddle hand is nearest that camera, so it is tracked rather than inferred",
    });
  }
  if (behind) {
    pairs.push({
      metrics: "Body rotation · footwork · split step",
      from: "the behind view",
      why: "torso turn only resolves off the front/back axis — it reads ~35% from the net side",
    });
  }

  const notes = [
    "Each angle is analysed on its own and the numbers are never averaged — a mean across two view geometries would describe neither camera.",
  ];
  if (side && !behind) {
    notes.push("A dead side-on view reads 0% body rotation. Don't judge torso turn from it.");
  }
  return { pairs, notes };
}
